import {Component, OnInit} from '@angular/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {BsModalRef} from 'ngx-bootstrap/modal/bs-modal-ref.service';
import {Observable, Subscription} from 'rxjs';
import {Subject} from 'rxjs/Rx';
import {environment} from '../../../environments/environment';
import {CARDGROUPINFO, CARDINFO, POOLINFO} from '../../../gen/swagger-api';
import {AlertService} from '../../shared/services/alert.service';
import {ColumnSortedEvent} from '../../shared/services/column-sort.service';
import {SubscriptionService} from '../../shared/services/subscription.service';
import {AuditModalComponent} from '../modals/audit-modal/audit-modal.component';
import {CardDetailsModalComponent} from '../modals/card-details/card-details-modal.component';
import {GenericOperationModalComponent} from '../modals/generic-operation/generic-operation-modal.component';
import {LoadSubscriptionModalComponent} from '../modals/load-subscription/load-subscription-modal.component';
import {UpdateConnectivityParametersModalComponent} from '../modals/update-connectivity-parameters/update-connectivity-parameters-modal.component';
import {LastSearch} from '../../shared/model/last-search';
import {FormControl, FormGroup} from '@angular/forms';
import {DefaultService} from '../../../gen/swagger-api/api/default.service';
import {AuthService} from '../../shared/services/auth/auth.service';
import {TableHeaderSearchModalComponent} from '../modals/table-header-search-modal/table-header-search-modal.component';
import {LastSearches} from '../../shared/model/last-searches';
import {ErrorHandlingService} from '../../shared/services/error-handling-service';

@Component({
  selector: 'app-main-page',
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.css']
})
export class MainPageComponent implements OnInit {

  /**
   * subscriptions to be shown in a table
   */
  public subscriptions: Array<CARDINFO> = [];
  operationName: string;
  bsModalRef: BsModalRef;
  public searchText: string;
  public searchProperty: string;
  offset = 0;
  static readonly LIMIT: number = 50;
  searchString$: Subject<string> = new Subject<string>();
  public onlyActiveCards: boolean = true;
  public selectedSubscriptionIds: Array<String> = [];
  public selectedCardGroupIds: Array<String> = [];
  public selectedPoolIds: Array<String> = [];
  public selectedStatus: Array<String> = [];
  lock = 0;

  operationsDisabled: boolean = this.selectedSubscriptionIds.length === 0;
  sortCriteria: ColumnSortedEvent;
  username: string;
  cardGroupsMap: any;
  cardGroups: Array<CARDGROUPINFO>;
  timerSubscription: Subscription;
  pools: Array<POOLINFO>;
  lastSearches: Array<String> = [];
  searchCtrl = new FormControl();
  searchForm = new FormGroup({
    searchCtrl: this.searchCtrl
  });

  constructor(
    private subscriptionService: SubscriptionService,
    private modalService: BsModalService,
    private defaultService: DefaultService,
    private alertService: AlertService,
    private authenticationService: AuthService,
    private errorHandlingService: ErrorHandlingService,
  ) {

    this.searchString$.debounceTime(500)
      .distinctUntilChanged()
      .do(searchText => this.searchText = searchText)
      .do(searchText => this.saveSearchText(searchText, this.searchProperty))
      .switchMap(searchText => {
        this.subscriptions = [];
        this.offset = 0;
        const filterParams = {
          selectedCardGroupIds: this.selectedCardGroupIds,
          selectedPoolIds: this.selectedPoolIds,
          selectedStatus: this.selectedStatus,
          onlyActiveCards: this.onlyActiveCards
        };
        return this.subscriptionService.subscriptions(this.offset, MainPageComponent.LIMIT, this.sortCriteria, searchText, this.searchProperty, filterParams);
      })
      .subscribe(subscription => {
        this.subscriptions = this.subscriptions.concat(subscription);
      });
  }

  ngOnInit() {
    // load initially subscriptions
    this.loadSubscriptions(MainPageComponent.LIMIT, 0, undefined, undefined, false);
    this.loadCardGroups();

    this.selectedSubscriptionIds = [];
    this.username = sessionStorage.getItem('username');
    this.timerSubscription = Observable.timer(environment.mainPageRefreshInterval).first().subscribe(() => this.refreshCallback());
  }

  public ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }


  /**
   * Sorting has been pushed.
   * Reset Offset and empty subscriptions: We receive new data from backend!
   * API supports sorting for: 'imsi,  - iccid,  - msisdn,  - mno_id,  - group_id',
   'card_group_id', 'pool_id'
   * @param $event string representing clicked sortCriteria
   */
  onSorted($event) {
    this.subscriptions = []; // reset subscriptionslist
    this.offset = 0; // reset offset
    this.sortCriteria = $event;
    this.refreshItemsManually();
  }

  public openGenericOperationModal(operationName: string) {
    const initialState = {
      operationName
    };

    this.bsModalRef = this.modalService.show(GenericOperationModalComponent, {initialState, class: 'modal-large'});
    this.bsModalRef.content.closeBtnName = 'Close';
  }

  public openAuditModal() {
    const subscriptions: Array<CARDINFO> = new Array<CARDINFO>();
    this.subscriptions.forEach(s => {
      if (this.selectedSubscriptionIds.indexOf(s.eis.eid) !== -1) {
        subscriptions.push(JSON.parse(JSON.stringify(s)));
      }
    });
    const initialState = {
      subscriptions
    };

    this.bsModalRef = this.modalService.show(AuditModalComponent, {initialState, class: 'modal-large'});
    this.bsModalRef.content.closeBtnName = 'Close';
    this.bsModalRef.content.action.take(1).subscribe((value) => {
      if (value) {
        this.refreshItemsManually();
      }
    });
  }

  public openLoadSubscriptionModal() {
    const subscriptions: Array<CARDINFO> = [];

    this.subscriptions.forEach(s => {
      if (this.selectedSubscriptionIds.indexOf(s.eis.eid) !== -1) {
        subscriptions.push(s);
      }
    });
    const initialState = {
      subscriptions
    };

    this.bsModalRef = this.modalService.show(LoadSubscriptionModalComponent, {initialState});
    this.bsModalRef.content.closeBtnName = 'Close';
  }

  public openUpdateConnectivityParametersModal() {
    this.bsModalRef = this.modalService.show(UpdateConnectivityParametersModalComponent, {});
    this.bsModalRef.content.closeBtnName = 'Close';
  }

  public openCardDetailsModal(event: Event, cardInfo: CARDINFO) {
    event.stopPropagation();
    const showButton = true;
    const initialState = {
      cardInfo,
      showButton
    };
    this.bsModalRef = this.modalService.show(CardDetailsModalComponent, {initialState, class: 'modal-large'});
    this.bsModalRef.content.closeBtnName = 'Close';
    this.bsModalRef.content.action.take(1).subscribe((value) => {
      this.refreshAllItems();
    });
  }

  /**
   * Loads Subscriptions and adds them to existing array of subscriptions.
   * In case you want to reload subscriptions, empty existing array first.
   *
   * @param limit
   * @param offset
   * @param searchString
   * @param searchProperty
   */
  public loadSubscriptions(limit: number = MainPageComponent.LIMIT, offset = 0, searchString?: string, searchProperty?: string, concat: boolean = true): void {
    const filterParams = {
      selectedCardGroupIds: this.selectedCardGroupIds,
      selectedPoolIds: this.selectedPoolIds,
      selectedStatus: this.selectedStatus,
      onlyActiveCards: this.onlyActiveCards
    };

    this.subscriptionService.subscriptions(offset, limit, this.sortCriteria, searchString, searchProperty, filterParams).subscribe(
      subscription => {
        if (this.lock === 0) {
          this.lock = 1;
          this.defaultService.poolsGet().subscribe(pools => {
              this.pools = pools;
              let tempSub = subscription;


              /*
              for (let i = 0; i < subscription.length; i++) {
                if (subscription[i].currentSubscription)
                {
                    let currentSubscription = subscription[i].currentSubscription;

                    if (currentSubscription.poolId && currentSubscription.poolId > 0) {
                      (tempSub[i] as any).poolName = this.getPoolNameByPoolId(currentSubscription.poolId).toString();
                    }
                    else {
                        if (currentSubscription.mnoId) {
                          (tempSub[i] as any).poolName = this.getPoolName(currentSubscription.mnoId).toString();
                        }
                    }
                 }
             }*/

              if (concat) {
                this.subscriptions = this.subscriptions.concat(tempSub);
              } else {
                this.subscriptions = tempSub;
              }
          },
          (error) => {
              // no pools available, let's set the subscriptions nevertheless:
              if (subscription != null && subscription.length > 0) {
                  this.subscriptions = subscription;
              } else {
                  console.error(error);
                  if (!error.status || error.status != 404) {
                      this.errorHandlingService.errorHandling(error);
                  }
              }
          }
          );
        }
      },
      (error) => {
      }
    );
  }

  public getPoolName(mnoId: string) {
    return this.pools.filter(x => x.mnoId === mnoId).map(y => y.poolName);
  }

  public getPoolNameByPoolId(poolId: number) {
    return this.pools.filter(x => x.poolId == poolId).map(y => y.poolName);
  }

  /**
   * Checks/Unchecks all elements in the table.
   * @param event containing checkbox-state
   */
  public checkAll(ev) {
    this.subscriptions.forEach(x => {
      if (ev.target.checked) {
        this.selectedSubscriptionIds.push(x.eis.eid);
      } else {
        this.selectedSubscriptionIds = [];
      }
    });
    this.operationsDisabled = this.selectedSubscriptionIds.length === 0;
  }

  public onToggleSelectedElement(subscription: CARDINFO) {
    const id = subscription.eis.eid;
    if (this.selectedSubscriptionIds.includes(id)) {
      const index = this.selectedSubscriptionIds.findIndex(d => d === id); // find index in your array
      this.selectedSubscriptionIds.splice(index, 1); // remove element from array
    } else {
      this.selectedSubscriptionIds.push(id);
    }
    this.operationsDisabled = this.selectedSubscriptionIds.length === 0;
  }

  public logout() {
    this.authenticationService.logout();
  }

  public onScrollDown() {
    this.offset = this.offset + MainPageComponent.LIMIT;
    this.loadSubscriptions(MainPageComponent.LIMIT, this.offset, this.searchText, this.searchProperty);
  }

  public onScrollUp() {
  }

  private loadCardGroups() {
    this.defaultService.cardGroupsGet().subscribe(cardGroups => {
      this.cardGroups = cardGroups;
      if (cardGroups !== null) {
        this.cardGroupsMap = cardGroups.reduce(function (map, obj) {
          map[obj.cardGroupId] = obj;
          return map;
        }, {});
      }
    });
  }

  private loadPools() {
    this.defaultService.poolsGet().subscribe(pools => {
        this.pools = pools;
      }
    );
  }

  public setSearchProperty(searchProperty: string) {
    this.searchProperty = searchProperty;
    this.subscriptions = [];
    // user set another searchProperty: retrigger loading of subscriptions
    this.loadSubscriptions(MainPageComponent.LIMIT, this.offset, this.searchText, this.searchProperty, false);
  }

  public refreshItemsManually() {
    this.lock = 0;
    this.loadSubscriptions(this.offset + MainPageComponent.LIMIT, 0, this.searchText, this.searchProperty, false);
  }

  public refreshAllItems() {
    this.lock = 0;
    this.loadSubscriptions(this.offset + MainPageComponent.LIMIT, 0, this.searchText, this.searchProperty, false);
  }

  public refreshCallback() {
    this.refreshAllItems();
    this.timerSubscription = Observable.timer(environment.mainPageRefreshInterval).first().subscribe(() => this.refreshCallback());
  }

  public showTableHeaderSearchModal(propertyName: string) {
    const pools = this.pools;
    const cardGroups = this.cardGroups;
    const selectedCardGroupIds = this.selectedCardGroupIds;
    const selectedPoolIds = this.selectedPoolIds;
    const selectedStatus = this.selectedStatus;

    const initialState = {
      propertyName,
      pools,
      cardGroups,
      selectedCardGroupIds,
      selectedPoolIds,
      selectedStatus
    };
    this.bsModalRef = this.modalService.show(TableHeaderSearchModalComponent, {initialState});
    this.bsModalRef.content.filterParams.subscribe(result => {
      this.selectedCardGroupIds = result.selectedCardGroupIds;
      this.selectedPoolIds = result.selectedPoolIds;
      this.selectedStatus = result.selectedStatus;
      this.refreshAllItems();
    });
  }

  /**
   *
   * @param searchText Save users SearchText for future use
   */
  private saveSearchText(searchText: string, searchProperty: string) {

    if (searchText == null || searchProperty == null) {
      return;
    }

    var lastSearches: LastSearches = JSON.parse(localStorage.getItem(this.username + '-lastsearches'));
    if (lastSearches == null) {
      lastSearches = new LastSearches();
    }
    var newLastSearch = new LastSearch();
    newLastSearch.setSearchText(searchText);
    newLastSearch.setSearchProperty(searchProperty);
    lastSearches.lastSearchList.push(newLastSearch);

    localStorage.setItem(this.username + '-lastsearches', JSON.stringify(lastSearches));
    this.lastSearches = lastSearches.lastSearchList.map(
      value => value.searchText
    );
  }

  setRowColor(status) {
    switch (status) {
      case 'WARNING':
        return 'orange';
      case 'FAILED':
        return 'red';
      case 'EXPIRED':
        return 'blue';
      case 'IN_PROCESS':
        return '#cfad02';
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/achelos-iot/main-page/main-page.component.ts